/**
 * CommunicatieSection — Centrale communicatie-inbox voor de backoffice.
 *
 * Sprint P0.3 · Communicatie Sprint (20 Feb 2026).  Aggregeert:
 *   - Herinneringen (reminder_schedule status=sent)
 *   - Uitgaande e-mails (email_logs)
 *   - Stripe-events (stripe_events)
 *
 * Endpoint: GET /api/admin/communications
 *
 * De pagina biedt:
 *   - Filter-pills op type (Alle · Herinneringen · E-mails · Stripe)
 *   - Zoekbalk (klant/factuurnr/tracking/subject)
 *   - Paginering
 *   - Uniforme Admin Design System componenten
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  RefreshCw, Loader2, Mail, Bell, CreditCard, Send,
  Inbox, MessageSquare, Zap,
} from "lucide-react";
import { Button } from "../../ui/button";
import {
  AdminPageHeader, AdminFilterTabs, AdminSectionCard,
  AdminEmptyState, AdminSkeleton, AdminSearchInput, AdminStatusBadge,
} from "../ui";
import { nlTerm } from "../../../utils/pluralize";
import CommunicatieDetailDialog from "./CommunicatieDetailDialog";
import ReminderComposer from "./ReminderComposer";
import { useAuth } from "../../../context/AuthContext";

const TYPE_FILTERS = [
  { key: "", label: "Alle" },
  { key: "reminder", label: "Herinneringen" },
  { key: "email", label: "E-mails" },
  { key: "stripe", label: "Stripe" },
];

const TYPE_META = {
  reminder: { icon: Bell,        label: "Herinnering",     tone: "warning" },
  email:    { icon: Mail,        label: "E-mail",          tone: "info" },
  stripe:   { icon: CreditCard,  label: "Stripe",          tone: "brand" },
};

function fmtMoney(v, cur = "EUR") {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: (cur || "EUR").toUpperCase(),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${cur} ${Number(v).toFixed(2)}`; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function CommunicatieSection() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailTarget, setDetailTarget] = useState(null); // {source, id}
  const [composerContext, setComposerContext] = useState(null); // reopen context
  const requestSequence = useRef(0);
  const limit = 30;

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (type) params.set("type", type);
      if (search) params.set("search", search);
      const r = await adminApi.get(`/admin/communications?${params.toString()}`);
      if (requestId !== requestSequence.current) return;
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (requestId === requestSequence.current) toast.error(formatApiError(e, "Communicatie laden mislukt"));
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [page, type, search]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="space-y-5" data-testid="admin-section-communicatie">
      <AdminPageHeader
        icon={MessageSquare}
        title="Communicatiecentrum"
        subtitle="Alle uitgaande klantcommunicatie — herinneringen, e-mails en Stripe events in één stroom"
        actions={
          <Button
            variant="outline" onClick={load} disabled={loading}
            className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500"
            data-testid="communicatie-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Vernieuwen
          </Button>
        }
      />

      {/* Filters */}
      <AdminSectionCard padded={false}>
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <AdminFilterTabs
            items={TYPE_FILTERS}
            value={type}
            onChange={(k) => { setType(k); setPage(1); }}
            testidPrefix="communicatie-filter"
          />
          <div className="ml-auto flex flex-wrap min-w-0 max-w-full items-center gap-3 [&>div]:min-w-0 [&>div]:max-w-full">
            <AdminSearchInput
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Zoek klant, factuur, tracking, onderwerp…"
              testid="communicatie-search"
            />
            <div className="text-xs text-ink-500 font-medium" data-testid="communication-message-count">
              {nlTerm("message", total)}
            </div>
          </div>
        </div>
      </AdminSectionCard>

      {/* List */}
      {loading ? (
        <AdminSkeleton variant="lines" rows={6} />
      ) : items.length === 0 ? (
        <AdminEmptyState
          icon={Inbox}
          title="Nog geen communicatie in deze weergave"
          description="Pas het filter aan of wacht op de eerstvolgende herinnering / betaal-event."
          testid="communicatie-empty"
        />
      ) : (
        <AdminSectionCard padded={false} testid="communicatie-list-card">
          <ul className="divide-y divide-slate-100">
            {items.map((it) => (
              <CommunicatieRow
                key={`${it.type}-${it.id}`}
                it={it}
                onOpen={() => setDetailTarget({ source: it.type, id: it.id })}
              />
            ))}
          </ul>
        </AdminSectionCard>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-ink-500">
            Pagina {page} / {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-full border-slate-200 hover:border-brand-500"
              data-testid="communicatie-prev"
            >
              Vorige
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * limit >= total}
              className="rounded-full border-slate-200 hover:border-brand-500"
              data-testid="communicatie-next"
            >
              Volgende
            </Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <CommunicatieDetailDialog
        open={!!detailTarget}
        onOpenChange={(v) => !v && setDetailTarget(null)}
        source={detailTarget?.source}
        messageId={detailTarget?.id}
        onReopenComposer={(ctx) => setComposerContext(ctx)}
      />

      {/* Reopen Composer */}
      <ReminderComposer
        open={!!composerContext}
        onOpenChange={(v) => !v && setComposerContext(null)}
        customerId={composerContext?.customer_id}
        focusInvoiceId={composerContext?.focus_invoice_id}
        currentUserEmail={user?.email || ""}
        onSent={() => load()}
      />
    </div>
  );
}

function CommunicatieRow({ it, onOpen }) {
  const meta = TYPE_META[it.type] || TYPE_META.email;
  const Icon = meta.icon;
  const statusTone = it.status === "sent" || it.status === "processed"
    ? "success"
    : it.status === "failed" || it.status === "bounced"
      ? "danger"
      : it.status === "queued"
        ? "warning"
        : "neutral";
  return (
    <li
      className="p-4 hover:bg-brand-50/40 transition-colors flex items-start gap-4 cursor-pointer"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen?.(); }}
      data-testid={`communicatie-row-${it.id}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        it.type === "reminder"
          ? "bg-amber-50 border border-amber-100 text-amber-700"
          : it.type === "stripe"
            ? "bg-brand-50 border border-brand-100 text-brand-500"
            : "bg-blue-50 border border-blue-100 text-blue-700"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <AdminStatusBadge tone={meta.tone} label={meta.label} size="xs" />
          <AdminStatusBadge tone={statusTone} label={it.status || "?"} size="xs" />
          {it.reminder_type && (
            <span className="text-[11px] font-mono text-ink-500">{it.reminder_type}</span>
          )}
          {it.stripe_event_type && (
            <span className="text-[11px] font-mono text-ink-500">{it.stripe_event_type}</span>
          )}
          <span className="ml-auto text-[11px] font-mono-num tabular-nums text-ink-500 whitespace-nowrap">
            {fmtDate(it.occurred_at)}
          </span>
        </div>
        <div className="mt-1 text-sm font-chivo font-bold text-brand-900 truncate">
          {it.subject || it.stripe_event_type || it.email_type || "—"}
        </div>
        <div className="mt-0.5 text-xs text-ink-500 flex items-center gap-3 flex-wrap">
          {it.customer_email && (
            <span className="truncate">
              <Mail className="w-3 h-3 inline mr-1" />
              {it.customer_name ? `${it.customer_name} · ` : ""}{it.customer_email}
            </span>
          )}
          {it.invoice_number && (
            <span className="font-mono-num tabular-nums text-brand-500">
              {it.invoice_number}
            </span>
          )}
          {it.tracking_number && (
            <span className="font-mono text-brand-500">{it.tracking_number}</span>
          )}
          {it.amount != null && (
            <span className="font-semibold text-brand-900">
              {fmtMoney(it.amount, it.currency)}
            </span>
          )}
          {it.sent_by && (
            <span className="text-ink-400"><Send className="w-3 h-3 inline mr-1" />{it.sent_by}</span>
          )}
        </div>
      </div>
    </li>
  );
}
